// Parser for Snapchat warrant returns: the CSV files Snap sends (subscriber_info.csv,
// ip_data.csv, conversations.csv, friends.csv, memories.csv, …), each made of
// "=====" / "-----" delimited sections. Plain JavaScript, no React or DOM: used by the
// Snapchat Warrant Processor page and by the Case Analyzer desktop app.

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseCsvLine(line) {
  const out = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === ',' && !inQ) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
}

// Parse all === blocks from a Snapchat CSV file → [{ label, headers, rows }]
function parseSections(text) {
  // Normalize line endings
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const result = [];
  let i = 0;
  let pendingLabel = '';

  // Strip trailing commas + whitespace — separator lines have extra commas in multi-column CSVs
  const bare = line => line.trimEnd().replace(/,+$/, '').trim();

  // Returns true for lines that are legend headers/content and should be skipped
  const isLegendOrJunk = line => {
    const b = bare(line);
    if (!b) return true;
    if (b.startsWith('Data Legend') || b.startsWith('Legend')) return true;
    if (/^-{5,}/.test(b)) return true;                  // "---------- Legend ---------"
    const raw = line.trimEnd().replace(/,+$/, '');
    if (raw.startsWith('  ') || raw.startsWith('\t')) return true;  // indented legend entry
    if (raw.startsWith('"  ') || raw.startsWith('" ')) return true; // quoted indented entry
    return false;
  };

  while (i < lines.length) {
    const b = bare(lines[i]);
    if (b === '---------------------------') {
      i++;
      // Capture section label, skip legend header/content lines
      while (i < lines.length) {
        const lb = bare(lines[i]);
        if (!lb) { i++; continue; }
        if (isLegendOrJunk(lines[i])) { i++; continue; }
        if (lb === '===========================' || lb === '---------------------------') break;
        pendingLabel = lb;
        i++;
        break;
      }
    } else if (b === '===========================') {
      i++;
      if (i >= lines.length) break;
      const firstBare = bare(lines[i]);
      if (!firstBare || firstBare.startsWith('NO RESPONSIVE')) { i++; pendingLabel = ''; continue; }
      const headers = parseCsvLine(lines[i]);
      i++;
      const rows = [];
      while (i < lines.length) {
        const rowBare = bare(lines[i]);
        if (rowBare === '---------------------------' || rowBare === '===========================') break;
        const content = lines[i].trim();
        if (content) rows.push(parseCsvLine(lines[i]));
        i++;
      }
      result.push({ label: pendingLabel, headers, rows });
      pendingLabel = '';
    } else {
      i++;
    }
  }
  return result;
}

function makeColMap(headers) {
  const m = {};
  headers.forEach((h, i) => { m[h.trim()] = i; });
  return m;
}
function col(row, cm, key) {
  const i = cm[key];
  return i !== undefined ? (row[i] || '').trim() : '';
}

// ─── Target info helpers ──────────────────────────────────────────────────────
function parseTargetLine(text) {
  const m = text.match(/Target phone number "([^"]+)" is associated with User ID "([^"]+)"/);
  return m ? { phone: m[1], userId: m[2] } : { phone: '', userId: '' };
}
function parseDateRange(text) {
  const m = text.match(/Date range searched: ([^\n\r]+)/);
  return m ? m[1].trim() : '';
}

// ─── File-specific parsers ────────────────────────────────────────────────────
function parseSubscriberInfo(text) {
  const secs = parseSections(text);
  const out = { main: {}, extended: {}, privacy: {}, bitmoji: {}, usernameHistory: [], accountChanges: [], homeWork: [] };
  for (const { label, headers, rows } of secs) {
    if (!rows.length) continue;
    const cm = makeColMap(headers);
    const r0 = rows[0];
    if (cm['username'] !== undefined) {
      out.main = {
        username:     col(r0, cm, 'username'),
        user_id:      col(r0, cm, 'user_id'),
        email:        col(r0, cm, 'email_address'),
        email_status: col(r0, cm, 'email_status'),
        created:      col(r0, cm, 'created'),
        creation_ip:  col(r0, cm, 'creation_ip'),
        phone:        col(r0, cm, 'phone_number'),
        phone_status: col(r0, cm, 'phone_status'),
        display_name: col(r0, cm, 'display_name'),
        status:       col(r0, cm, 'status'),
      };
    } else if (cm['old_value'] !== undefined && label.toLowerCase().includes('username')) {
      out.usernameHistory = rows.map(r => ({ old: col(r, cm, 'old_value'), new: col(r, cm, 'new_value'), timestamp: col(r, cm, 'timestamp') }));
    } else if (cm['email_verified_timestamp'] !== undefined) {
      out.extended = {
        email_verified: col(r0, cm, 'email_verified_timestamp'),
        phone_verified: col(r0, cm, 'phone_verified_timestamp'),
        birthdate:      col(r0, cm, 'birthdate'),
        last_active:    col(r0, cm, 'last_active'),
        follower_count: col(r0, cm, 'follower_count'),
        app_version:    col(r0, cm, 'app_version'),
        two_fa:         col(r0, cm, '2FA_status'),
      };
    } else if (cm['snap_privacy'] !== undefined) {
      out.privacy = { snap: col(r0, cm, 'snap_privacy'), story: col(r0, cm, 'story_privacy') };
    } else if (cm['is_bitmoji_user'] !== undefined) {
      out.bitmoji = { is_bitmoji: col(r0, cm, 'is_bitmoji_user'), gender: col(r0, cm, 'bitmoji_gender') };
    } else if (cm['action'] !== undefined) {
      out.accountChanges = rows.map(r => ({
        date: col(r, cm, 'date'), action: col(r, cm, 'action'),
        old_value: col(r, cm, 'old_value'), new_value: col(r, cm, 'new_value'), reason: col(r, cm, 'reason'),
      }));
    } else if (cm['place'] !== undefined) {
      out.homeWork = rows.map(r => ({ place: col(r, cm, 'place'), latitude: col(r, cm, 'latitude'), longitude: col(r, cm, 'longitude'), isHidden: col(r, cm, 'isHidden') }));
    }
  }
  return out;
}

function parseIpData(text) {
  const secs = parseSections(text);
  const out = [];
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['ip'] === undefined) continue;
    for (const r of rows) {
      out.push({ ip: col(r, cm, 'ip'), asn: col(r, cm, 'asn'), timestamp: col(r, cm, 'timestamp'), user_agent: col(r, cm, 'user_agent'), device_id: col(r, cm, 'device_id'), source: col(r, cm, 'source'), port: col(r, cm, 'client_source_port') });
    }
  }
  return out;
}

function parseConversations(text, targetUserId) {
  const secs = parseSections(text);
  const groups = {};
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['conversation_id'] === undefined) continue;
    for (const r of rows) {
      const cid = col(r, cm, 'conversation_id');
      if (!cid) continue;
      if (!groups[cid]) {
        groups[cid] = {
          cid, title: col(r, cm, 'conversation_title'),
          isOneOnOne: col(r, cm, 'is_one_on_one') === 'TRUE',
          participants: new Set(), messages: [],
        };
      }
      const g = groups[cid];
      const senderU = col(r, cm, 'sender_username');
      const recipU  = col(r, cm, 'recipient_username');
      const senderId = col(r, cm, 'sender_user_id');
      if (senderU) g.participants.add(senderU);
      if (recipU) g.participants.add(recipU);
      col(r, cm, 'group_member_usernames').split(';').map(s => s.trim()).filter(Boolean).forEach(u => g.participants.add(u));
      g.messages.push({
        content_type: col(r, cm, 'content_type'), sender: senderU, sender_id: senderId,
        recipient: recipU, text: col(r, cm, 'text'), media_id: col(r, cm, 'media_id'),
        is_saved: col(r, cm, 'is_saved'), timestamp: col(r, cm, 'timestamp'),
        upload_ip: col(r, cm, 'upload_ip'), screenshotted_by: col(r, cm, 'screenshotted_by'),
      });
    }
  }
  return Object.values(groups).map(g => {
    const msgs = g.messages.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    const parts = [...g.participants];
    let title = g.title;
    if (!title && g.isOneOnOne) {
      const targetMsg = msgs.find(m => m.sender_id === targetUserId);
      title = targetMsg ? (parts.find(p => p !== targetMsg.sender) || parts[0] || 'Unknown') : (parts[0] || 'Unknown');
    }
    return { cid: g.cid, title: title || 'Group Chat', isOneOnOne: g.isOneOnOne, participants: parts, messages: msgs, lastTimestamp: msgs[msgs.length - 1]?.timestamp || '' };
  }).sort((a, b) => b.lastTimestamp.localeCompare(a.lastTimestamp));
}

function parseFriends(text) {
  const secs = parseSections(text);
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['adder'] === undefined) continue;
    return rows.map(r => ({ adder: col(r, cm, 'adder'), added: col(r, cm, 'added'), type: col(r, cm, 'type'), created: col(r, cm, 'created'), friending_method: col(r, cm, 'friending_method') }));
  }
  return [];
}

function parseFriendsList(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const bare = line => line.trimEnd().replace(/,+$/, '').trim();
  const sepIdx = lines.findIndex(l => bare(l) === '===========================');
  if (sepIdx === -1) return [];
  const usernames = [];
  for (let i = sepIdx + 1; i < lines.length; i++) {
    const b = bare(lines[i]);
    if (!b || b.startsWith('NO RESPONSIVE') || b === '---------------------------' || b === '===========================') break;
    usernames.push(b);
  }
  return usernames;
}

function parseMemories(text) {
  const secs = parseSections(text);
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['media_id'] === undefined) continue;
    return rows.map(r => ({ id: col(r, cm, 'id'), media_id: col(r, cm, 'media_id'), latitude: col(r, cm, 'latitude'), longitude: col(r, cm, 'longitude'), duration: col(r, cm, 'duration'), timestamp: col(r, cm, 'timestamp'), source_type: col(r, cm, 'source_type'), encrypted: col(r, cm, 'encrypted') }));
  }
  return [];
}

function parseStory(text) {
  const secs = parseSections(text);
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['timestamp'] === undefined || cm['media_id'] === undefined) continue;
    return rows.map(r => ({ media_id: col(r, cm, 'media_id'), username: col(r, cm, 'username'), media_type: col(r, cm, 'media_type'), timestamp: col(r, cm, 'timestamp'), upload_ip: col(r, cm, 'upload_ip'), source_port: col(r, cm, 'source_port_number') }));
  }
  return [];
}

function parsePublicProfile(text) {
  const secs = parseSections(text);
  for (const { headers, rows } of secs) {
    const cm = makeColMap(headers);
    if (cm['created_at'] === undefined || !rows.length) continue;
    const r = rows[0];
    return { created_at: col(r, cm, 'created_at'), display_name: col(r, cm, 'display_name'), is_frozen: col(r, cm, 'is_frozen'), designation: col(r, cm, 'public_user_designation'), stated_location: col(r, cm, 'stated_location'), subscriber_count: col(r, cm, 'subscriber_count'), bio: col(r, cm, 'bio'), website: col(r, cm, 'website') };
  }
  return null;
}

function parseGenericTable(text) {
  return parseSections(text).filter(s => s.rows.length > 0);
}

// ─── Master parse ─────────────────────────────────────────────────────────────
export function parseSnapchatReturn(fileMap) {
  const get = name => fileMap[name] || '';
  const subText = get('subscriber_info.csv');
  const targetInfo = parseTargetLine(subText);
  const dateRange  = parseDateRange(subText);
  const subscriber = parseSubscriberInfo(subText);
  const ipRecords  = parseIpData(get('ip_data.csv'));
  const conversations = parseConversations(get('conversations.csv'), targetInfo.userId);
  const friends    = parseFriends(get('friends.csv'));
  const friendsList = parseFriendsList(get('friends_list.csv'));
  const memories   = parseMemories(get('memories.csv'));
  const stories    = parseStory(get('story.csv'));
  const publicProfile = parsePublicProfile(get('public_profile.csv'));
  const aiConvos   = parseGenericTable(get('ai_conversations.csv'));
  const groupStory = parseGenericTable(get('group_story.csv'));
  const publicStory = parseGenericTable(get('public_story.csv'));
  const locPriv    = parseGenericTable(get('loc_priv_sets.csv'));
  const emailEvents = parseGenericTable(get('snap_inc_communications.csv'));
  const reportedConvos = parseGenericTable(get('reported_conversations.csv'));
  const reportedMedia  = parseGenericTable(get('reported_media_metadata.csv'));
  const sharedStory    = parseGenericTable(get('shared_story.csv'));
  const detectedUrl    = parseGenericTable(get('detected_url.csv'));

  const ipCounts = {};
  for (const r of ipRecords) if (r.ip) ipCounts[r.ip] = (ipCounts[r.ip] || 0) + 1;
  const topIps = Object.entries(ipCounts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([ip, count]) => ({ ip, count }));

  return { targetInfo, dateRange, subscriber, ipRecords, topIps, conversations, friends, friendsList, memories, stories, publicProfile, aiConvos, groupStory, publicStory, locPriv, emailEvents, reportedConvos, reportedMedia, sharedStory, detectedUrl };
}
